import type { IconAnimation, IconData, IconStyle } from "./types.js";

/** Every icon animation runs this long by default, and starts and ends on the resting drawing. */
export const ICON_ANIMATION_MS = 1200;

interface Playing {
  anims: Animation[];
  rest: string;
}

const playing = new WeakMap<SVGSVGElement, Playing>();
let uid = 0;

const reducedMotion = () =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Where the Web Animations API is missing (an old browser, a test DOM), icons stay still. */
const canAnimate = (svg: SVGSVGElement) => typeof svg.animate === "function";

/** Each icon's animation once it has loaded, and the load in flight before then. */
const loaded = new WeakMap<IconData, IconAnimation>();
const loading = new WeakMap<IconData, Promise<IconAnimation | null>>();

/**
 * Loads `icon`'s animation module, once per icon however often it is asked. Resolves to null when
 * the module can't be fetched (the icon then stays still). Call it ahead of a likely play, e.g.
 * when the page is idle, so the first play starts at once.
 */
export function preloadIcon(icon: IconData): Promise<IconAnimation | null> {
  const done = loaded.get(icon);
  if (done) return Promise.resolve(done);
  let pending = loading.get(icon);
  if (!pending) {
    pending = icon.animation().then(
      (module) => {
        loaded.set(icon, module.default);
        return module.default;
      },
      () => {
        loading.delete(icon); // a later play may try again
        return null;
      },
    );
    loading.set(icon, pending);
  }
  return pending;
}

/** How one play runs. */
export interface PlayIconOptions {
  /** Length of the run in ms; the keyframes are offsets, so they stretch to fit. Defaults to `ICON_ANIMATION_MS`. */
  duration?: number;
}

/**
 * Plays `icon`'s animation once on `svg`, an svg already showing `icon.styles[style].svg` (with `{id}` filled in).
 * A second call while it plays lets it finish; it does nothing under reduced motion or without the
 * Web Animations API. The first play of an icon whose animation hasn't loaded starts once it has
 * (see `preloadIcon`).
 */
export function playIcon(
  svg: SVGSVGElement,
  icon: IconData,
  style: IconStyle,
  options: PlayIconOptions = {},
): void {
  const current = playing.get(svg);
  if (current) {
    for (const a of current.anims) a.updatePlaybackRate(1);
    return;
  }
  if (reducedMotion() || !canAnimate(svg)) return;
  const animation = loaded.get(icon);
  if (animation) {
    start(svg, animation, style, options);
    return;
  }
  void preloadIcon(icon).then((ready) => {
    // the icon may have left the page, or started playing, while its animation loaded
    if (ready && svg.isConnected && !playing.has(svg)) start(svg, ready, style, options);
  });
}

function start(
  svg: SVGSVGElement,
  animation: IconAnimation,
  style: IconStyle,
  { duration = ICON_ANIMATION_MS }: PlayIconOptions,
): void {
  const rest = svg.innerHTML;
  // mask/clip ids are made unique per play so two copies of an icon can animate at once
  svg.innerHTML = animation.styles[style].replaceAll("{id}", `ica${++uid}-`);
  const anims = Array.from(svg.querySelectorAll<SVGElement>("[data-t]"), (el) =>
    el.animate(animation.tracks[Number(el.dataset.t)] ?? null, duration),
  );
  const entry = { anims, rest };
  playing.set(svg, entry);
  Promise.all(anims.map((a) => a.finished)).then(
    () => {
      if (playing.get(svg) !== entry) return;
      playing.delete(svg);
      svg.innerHTML = rest;
    },
    () => {},
  );
}

/** Speeds up a playing animation so it finishes sooner, e.g. when the pointer leaves. */
export function hurryIcon(svg: SVGSVGElement): void {
  for (const a of playing.get(svg)?.anims ?? []) a.updatePlaybackRate(1.7);
}

/** Stops a playing animation and puts the resting drawing back. */
export function stopIcon(svg: SVGSVGElement): void {
  const current = playing.get(svg);
  if (!current) return;
  playing.delete(svg);
  for (const a of current.anims) a.cancel();
  svg.innerHTML = current.rest;
}
